import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, Download, FileCheck2, PackageOpen } from 'lucide-react';
import { type TenantExportDto, type TenantExportLinkDto } from '@his/shared';
import { get, post } from '@/lib/api';
import { dateTime } from '@/lib/utils';
import { statusLabel } from '@/components/ui/badge';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Field, Input, Textarea } from '@/components/ui/primitives';
import { OwnerConfirmDialog } from './OwnerConfirm';
import { useOwnerCapability } from './owner-nav';

export interface ClosureFacts {
  id: string;
  slug: string;
  environment?: 'live' | 'demo';
  deploymentMode?: 'CLOUD' | 'LOCAL';
  closureNoticeAt?: string | null;
  closureEndsAt?: string | null;
  deletionDueAt?: string | null;
  closureAccess?: 'everyone' | 'admins' | null;
  exportCompletedAt?: string | null;
  exportDeliveredRef?: string | null;
  exportWaived?: boolean;
  exportReleasedAt?: string | null;
  purgeHeldReason?: string | null;
}

type Action = 'cancel' | 'export' | 'link' | 'delivered' | 'waiver' | 'release';

const STATUS_BADGE: Record<TenantExportDto['status'], 'default' | 'success' | 'warning' | 'destructive' | 'muted'> = {
  queued: 'default',
  running: 'warning',
  ready: 'success',
  downloaded: 'muted',
  failed: 'destructive',
  expired: 'muted',
  cancelled: 'muted',
};

export function TenantClosurePanel({ facts }: { facts: ClosureFacts }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canClose = useOwnerCapability('tenant.closure');
  const canExport = useOwnerCapability('tenant.export');
  const [action, setAction] = useState<Action | null>(null);
  const [target, setTarget] = useState<string | null>(null);
  const [reference, setReference] = useState('');
  const [waiver, setWaiver] = useState('');
  const [attachment, setAttachment] = useState('');

  const exports = useQuery({
    queryKey: ['tenant-exports', facts.id],
    queryFn: () => get<TenantExportDto[]>(`/platform/tenants/${facts.id}/exports`),
    staleTime: 60_000,
  });

  const done = () => {
    setAction(null);
    setTarget(null);
    setReference('');
    setWaiver('');
    setAttachment('');
    void queryClient.invalidateQueries({ queryKey: ['tenant', facts.id] });
    void queryClient.invalidateQueries({ queryKey: ['tenant-exports', facts.id] });
  };

  const scheduled = Boolean(facts.closureNoticeAt || facts.deletionDueAt);
  const delivered = Boolean(facts.exportCompletedAt || facts.exportWaived);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Archive className="h-4 w-4" /> Closure
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {scheduled ? (
            <Alert variant="warning">
              {facts.closureEndsAt && facts.closureNoticeAt && facts.closureEndsAt !== facts.closureNoticeAt ? `Notice until ${dateTime(facts.closureEndsAt)}. ` : ''}
              {facts.deletionDueAt ? `Deletion due ${dateTime(facts.deletionDueAt)}.` : ''}
              {facts.purgeHeldReason === 'export' ? ' Held: the facility has not received its data yet.' : ''}
              {facts.purgeHeldReason === 'size' ? ' Held: too large to delete automatically; run the purge by hand.' : ''}
            </Alert>
          ) : (
            <p className="text-muted-foreground">No closure is scheduled.</p>
          )}
          <p>
            Data delivered:{' '}
            {facts.exportCompletedAt ? (
              <Badge variant="success">Yes, {dateTime(facts.exportCompletedAt)}</Badge>
            ) : facts.exportWaived ? (
              <Badge variant="warning">Waived</Badge>
            ) : (
              <Badge variant="muted">Not yet</Badge>
            )}
            {facts.exportDeliveredRef && <span className="ms-2 text-xs text-muted-foreground">ref {facts.exportDeliveredRef}</span>}
          </p>
          {canClose && (
            <div className="flex flex-wrap gap-2">
              {scheduled ? (
                <Button type="button" variant="outline" onClick={() => setAction('cancel')}>
                  Cancel closure
                </Button>
              ) : (
                <Button type="button" variant="destructive" onClick={() => navigate(`/owner/tenants/${facts.id}/closure`)}>
                  {facts.environment === 'demo' ? 'Delete demo facility' : 'Close facility'}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <PackageOpen className="h-4 w-4" /> Full data export
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            One export runs at a time, at night in the facility&apos;s time. The file can be downloaded once, within 7 days.
          </p>
          {(exports.data ?? []).length === 0 ? (
            <p className="text-muted-foreground">No exports yet.</p>
          ) : (
            <ul className="divide-y">
              {(exports.data ?? []).map((row) => (
                <li key={row.id} className="flex flex-wrap items-center gap-2 py-2">
                  <Badge variant={STATUS_BADGE[row.status]}>{statusLabel(row.status, 'tenant_export_status')}</Badge>
                  <span className="text-xs text-muted-foreground">
                    {row.requestedByKind === 'super_admin' ? 'Asked by the facility' : 'Asked by the owner'} · {dateTime(row.queuedAt)}
                    {row.status === 'queued' && row.notBefore ? ` · starts after ${dateTime(row.notBefore)}` : ''}
                    {row.status === 'queued' && row.queuePosition ? ` · position ${row.queuePosition}` : ''}
                    {row.sizeBytes ? ` · ${Math.round(row.sizeBytes / 1024 / 1024)} MB` : ''}
                    {row.error ? ` · ${row.error}` : ''}
                  </span>
                  {canExport && row.status === 'ready' && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setTarget(row.id);
                        setAction('link');
                      }}
                    >
                      <Download className="h-3.5 w-3.5" /> Download
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {canExport && (
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={() => setAction('export')}>
                Queue an export
              </Button>
              {!delivered && (
                <Button type="button" variant="ghost" onClick={() => setAction('delivered')}>
                  <FileCheck2 className="h-3.5 w-3.5" /> Record delivery
                </Button>
              )}
              {!delivered && (
                <Button type="button" variant="ghost" onClick={() => setAction('waiver')}>
                  Record a waiver
                </Button>
              )}
              <Button type="button" variant="ghost" onClick={() => setAction('release')}>
                Allow one export during a hold
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {action === 'cancel' && (
        <OwnerConfirmDialog
          open
          onClose={() => setAction(null)}
          title="Cancel the closure"
          description={facts.slug}
          confirmLabel="Cancel closure"
          successMessage="Closure cancelled"
          action={(payload) => post(`/platform/tenants/${facts.id}/cancel-closure`, payload)}
          onDone={done}
        />
      )}
      {action === 'export' && (
        <OwnerConfirmDialog
          open
          onClose={() => setAction(null)}
          title="Queue a full data export"
          description={facts.slug}
          confirmLabel="Queue export"
          successMessage="Export queued"
          action={(payload) => post(`/platform/tenants/${facts.id}/exports`, payload)}
          onDone={done}
        />
      )}
      {action === 'link' && target && (
        <OwnerConfirmDialog<TenantExportLinkDto>
          open
          onClose={() => setAction(null)}
          title="Download the export"
          description="The link works once, for 60 seconds."
          confirmLabel="Download"
          action={(payload) => post<TenantExportLinkDto>(`/platform/tenants/${facts.id}/exports/${target}/link`, payload)}
          onDone={(link) => {
            window.location.assign(link.url);
            done();
          }}
        />
      )}
      {action === 'delivered' && (
        <OwnerConfirmDialog
          open
          onClose={() => setAction(null)}
          title="Record that the facility has its data"
          description="For a handover made some other way, e.g. a disk collected in person."
          extraValid={reference.trim().length >= 3}
          confirmLabel="Record delivery"
          successMessage="Delivery recorded"
          action={(payload) => post(`/platform/tenants/${facts.id}/export-delivered`, { ...payload, reference: reference.trim() })}
          onDone={done}
        >
          <Field label="Reference">
            <Input value={reference} onChange={(event) => setReference(event.target.value)} placeholder="Receipt number, ticket, courier reference…" />
          </Field>
        </OwnerConfirmDialog>
      )}
      {action === 'waiver' && (
        <OwnerConfirmDialog
          open
          onClose={() => setAction(null)}
          title="Record the facility's waiver"
          description="The facility's written statement that it does not want its data."
          extraValid={waiver.trim().length >= 20 && attachment.trim().length >= 3}
          confirmLabel="Record waiver"
          successMessage="Waiver recorded"
          action={(payload) => post(`/platform/tenants/${facts.id}/export-waiver`, { ...payload, waiver: waiver.trim(), attachmentReference: attachment.trim() })}
          onDone={done}
        >
          <Field label="Waiver text">
            <Textarea rows={4} value={waiver} onChange={(event) => setWaiver(event.target.value)} />
          </Field>
          <Field label="Where the signed copy is kept">
            <Input value={attachment} onChange={(event) => setAttachment(event.target.value)} placeholder="Ticket attachment, document number…" />
          </Field>
        </OwnerConfirmDialog>
      )}
      {action === 'release' && (
        <OwnerConfirmDialog
          open
          onClose={() => setAction(null)}
          title="Allow one export during a hold"
          description="The facility's Super Admin may download all data once while a security or compliance hold is in place."
          confirmLabel="Allow one export"
          successMessage="One export allowed"
          action={(payload) => post(`/platform/tenants/${facts.id}/release-export`, payload)}
          onDone={done}
        />
      )}
    </div>
  );
}
